/**
 * EAP Benefit Definitions
 *
 * Defines the structure and cell specifications for Employee Assistance Program benefits.
 */

import {
  BaseBenefitPlanPolicyPeriod,
  EmployerContributionEnum,
  extendEnumWithNotDisclosed,
  RateGuaranteeTypeEnum,
  RfpInclusionStatusEnum,
  RfpTiedToAnotherLocEnum,
} from "@platform/openapi-types";

import { BenefitDefinitions } from "../../benefits/benefit.types";
import {
  createEnumFormatter,
  createValueFormatter,
} from "../../components/PlanEditor/utilities/enumFormatters";
import * as sharedCellSpecs from "../shared/cellSpecs/common";
import { employerContributionLabels } from "../shared/definitions/customLabels";
import {
  buildCommissionBenefits,
  buildTypeAmountBenefitPair,
} from "../shared/definitions/builders";

// Pre-build commission definitions to override isRequired on type fields
const commissionDefs = buildCommissionBenefits();

export const benefits: BenefitDefinitions = {
  // Current Carrier (nested under Coverage Opportunity on BenefitPlan level)
  currentCarrierName: {
    label: "Current Carrier",
    description: "Name of the current insurance carrier",
    cell: sharedCellSpecs.currentCarrierName,
    networkDependent: false,
    benefitPlanLevel: true,
  },

  // ========================================
  // COMMISSIONS
  // ========================================

  ...commissionDefs,
  // Override: RFP requires commission type fields to be required
  currentCommissionType: {
    ...commissionDefs["currentCommissionType"],
    isRequired: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },
  requestedCommissionType: {
    ...commissionDefs["requestedCommissionType"],
    isRequired: true,
  },

  // Eligibility Classes (benefit plan group level)
  eligibilityClasses: {
    label: "Eligibility Class",
    description: "Benefit classes assigned to this benefit plan group",
    cell: sharedCellSpecs.eligibilityClasses,
    benefitPlanLevel: true,
    isRequired: true,
  },

  // Face to Face Visits
  faceToFaceVisits: {
    label: "Face to Face Visits",
    description: "Indicates the number of face to face visits covered",
    cell: sharedCellSpecs.numberField,
    networkDependent: false,
  },

  // Per Occurrence Per Year
  perOccurrencePerYear: {
    label: "Per Occurrence / Per Year",
    description:
      "Indicates the frequency at which the face to face visits are provided",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },

  // Unlimited Telephonic
  unlimitedTelephonic: {
    label: "Unlimited Telephonic",
    description: "Indicates if telephonic visits are unlimited or not",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(RfpInclusionStatusEnum),
      },
    },
    networkDependent: false,
  },

  // Legal & Financial Resources
  legalFinancialResources: {
    label: "Legal & Financial Resources",
    description:
      "Indicates if legal and financial resources are part of the EAP plan",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },

  // Extended (non-v5) field. The backend field is a plain string for now
  // (RFC-004), not yet an enum, so this is text rather than a dropdown.
  // Placed between Legal & Financial Resources and Tied to Another LOC to
  // match the presentation's row order (Chameleon's Eap.BenefitRows).
  additionalResourcesIncluded: {
    mpOnly: true,
    label: "Add'l Resources Included",
    mpConfigKey: "additional_resources_included",
    description: "Additional resources included with the EAP plan",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },

  // Tied to Another LOC
  tiedToAnotherLoc: {
    label: "Tied to Another LOC",
    description: "Indicates which, if any product the EAP is tied to",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(RfpTiedToAnotherLocEnum),
        formatValue: createEnumFormatter({
          voluntaryLifeAdnd: "Vol. Life/AD&D",
          basicLifeAdnd: "Basic Life/AD&D",
        }),
      },
      valueFormatter: createValueFormatter({
        voluntaryLifeAdnd: "Vol. Life/AD&D",
        basicLifeAdnd: "Basic Life/AD&D",
      }),
    },

    networkDependent: false,
  },

  // Employer Contribution
  employerContribution: {
    label: "Employer Contribution",
    description: "Level of employer contribution",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(EmployerContributionEnum),
        formatValue: createEnumFormatter(employerContributionLabels),
      },
      valueFormatter: createValueFormatter(employerContributionLabels),
    },
    networkDependent: false,
  },

  participationRequirements: {
    label: "Participation Requirements",
    description: "Participation requirement details for the benefit plan",
    cell: sharedCellSpecs.text,
    networkDependent: false,
    benefitPlanLevel: true,
    requestedPlanRowVisibility: {
      field: "employerContribution",
      equals: EmployerContributionEnum.voluntary,
    },
  },

  // Rate Guarantee - using builder
  ...buildTypeAmountBenefitPair({
    prefix: "rateGuarantee",
    typeLabel: "Rate Guarantee",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: Object.keys(RateGuaranteeTypeEnum),
    amountCell: sharedCellSpecs.numberField,
    typeDescription:
      "Time frame in which rates are guaranteed to stay the same",
    amountDescription: "Number of months or years the rate is guaranteed",
    amountDefaultValue: 1,
    disabledValues: [
      RateGuaranteeTypeEnum.notIncluded,
      RateGuaranteeTypeEnum.notOffered,
    ],
  }),
} as const;
